import { columns, drawerFilters } from './helpers'
import { useMemo, useState } from "react"
import { toTableRowItems } from './serializers'
import s from '../../../commonStyles.modules.scss'
import { useNotification } from 'hooks'
import { Table } from 'components/core-ui/Table'
import { makeCoreGetRequest, makeCorePutRequest } from "utils/api"
import { toTableGETUrl } from 'components/core-ui/Table/serializers'
import { formatErrorMessage } from 'utils/formatting'
import { AddActionIcon, DeleteActionIcon } from "icons"
import ImportProducts from './ImportProducts/ImportProducts'
import SensitiveProduct from './SensitiveProduct/SensitiveProduct'
import {
  getMetiFamiliesOptions,
  getMetiSubFamiliesOptions,
  getMetiUBOptions,
  getRayonsOptions,
  getScreenPermissions,
  getSensitiveShopTypes,
  getShopOptions
} from "../../api"
import { SCREENS } from '../../constants'
import { useMutation } from "react-query"
import { getUserRoles } from "../../../../../utils/authService"
import { isAllowed } from "../../../../../utils/helpers"
import { PermissionRoles } from "../../../../Layout/constants"

const SensitiveProducts = () => {
  const { showNotification } = useNotification()

  const [tableItems, setTableItems] = useState<SensitiveProduct[]>([])
  const [totalCount, setTotalCount] = useState<number>(0)
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [shouldRefreshData, setShouldRefreshData] = useState<boolean>(false)
  const [createUpdateModal, setCreateUpdateModal] = useState<string>(null)

  const openCreateModal = (): void => setCreateUpdateModal('create')
  const closeCreateUpdateModal = (): void => setCreateUpdateModal(null)

  const [isImportProductsModalOpen, setIsImportProductsModalOpen] = useState<boolean>(false)
  const openImportProductsModal = (): void => setIsImportProductsModalOpen(true)
  const closeImportProductsModal = (): void => setIsImportProductsModalOpen(false)

  const { data: permissions } = getScreenPermissions(showNotification, SCREENS.ADMIN_SENSITIVE)
  const currentUserRoles = useMemo(() => getUserRoles(), [])

  const isAdmin = isAllowed(PermissionRoles.GG_RO_HQ_SHOP_RECEPTION_ADMIN, currentUserRoles)

  const deleteSensitiveProductRequest = async ({ id }: { id: number }) => {
    return await makeCorePutRequest(`/shop-reception/products/sensitives/${id}?isDeleted=true`, {})
  }

  const { mutate: deleteSensitiveProduct } = useMutation(
    deleteSensitiveProductRequest,
    {
      onSuccess: () => {
        showNotification({ message: 'Produsul a fost sters cu succes.', type: 'success' })
        setShouldRefreshData(true)
      },
      onError: err => {
        showNotification({
          message: `Nu s-a putut sterge produsul. ${formatErrorMessage(err)}`,
          type: 'error'
        })
      }
    }
  )

  const tableColumns = useMemo(() => columns(), [])

  const fetchData = async ({ page, rowsPerPage, order, orderBy, tableFilters }) => {
    setIsLoading(true)
    try {
      const response = await makeCoreGetRequest(
        toTableGETUrl({
          url: '/shop-reception/products/sensitives',
          tableFilters,
          page,
          rowsPerPage,
          order,
          orderBy
        })
      )

      const responseList = response.list

      setTableItems(toTableRowItems(responseList))
      setTotalCount(response.page.totalCount)
      setIsLoading(false)
    } catch (error) {
      showNotification({
        message: `A aparut o eroare la obtinerea datelor din tabel. ${formatErrorMessage(error)}`,
        type: 'error'
      })
    }
  }

  const { data: shopCodes, isLoading: isShopCodesLoading } = getShopOptions(showNotification)
  const { data: shopTypes } = getSensitiveShopTypes(showNotification)
  const { data: metiRayons } = getRayonsOptions(showNotification)
  const { data: metiFamilies } = getMetiFamiliesOptions(showNotification)
  const { data: metiSubfamilies } = getMetiSubFamiliesOptions(showNotification)
  const { data: metiUB } = getMetiUBOptions(showNotification)

  return (
    <div className={s.mainContainer}>
      <ImportProducts
        isModalOpen={isImportProductsModalOpen}
        closeModal={closeImportProductsModal}
        setShouldRefreshData={setShouldRefreshData}
        shopCodes={shopCodes}
        shopTypesOptions={shopTypes}
        isShopCodesLoading={isShopCodesLoading}
        canUseRules={isAdmin}
      />
      <SensitiveProduct
        isModalOpen={createUpdateModal === 'create'}
        closeModal={closeCreateUpdateModal}
        setShouldRefreshData={setShouldRefreshData}
        shopCodes={shopCodes}
        shopTypesOptions={shopTypes}
        canUseRules={isAdmin}
      />

      <Table
        hideCheckbox
        columns={tableColumns}
        items={tableItems}
        fetchData={fetchData}
        isLoading={isLoading}
        totalCount={totalCount}
        shouldRefreshData={shouldRefreshData}
        setShouldRefreshData={setShouldRefreshData}
        rowActions={
          isAdmin ?
            [{
              label: 'Sterge',
              labelColor: 'white',
              typographyVariant: 'medium',
              backgroundColor: '#16a085',
              desktopIcon: <DeleteActionIcon />,
              onClick: (e, rowId) => {
                const row = tableItems.find(x => x.id === rowId)
                if (!row) return
                deleteSensitiveProduct({ id: row.id })
              }
            }]
            : []
        }
        rowActionsWidth="130px"
        toolbarActions={
          permissions?.canAdd
            ? [
                {
                  label: 'Importǎ produse sensibile',
                  icon: <AddActionIcon />,
                  onClick: openImportProductsModal
                },
                {
                  label: 'Produse sensibile',
                  icon: <AddActionIcon />,
                  onClick: openCreateModal
                }
              ]
            : []
        }
        filters={drawerFilters(shopCodes, shopTypes, metiRayons, metiFamilies, metiSubfamilies, metiUB)}
        filtersLabel="Filtreaza"
        filtersButtonLabel="Filtreaza"
        appliedFiltersLabel="Filtre"
        cancelFiltersButtonLabel="Anuleaza"
        applyFiltersButtonLabel="Aplica"
        rowsPerPage={10}
        rowsPerPageOptions={[10, 25, 50]}
      />
    </div>
  )
}

export default SensitiveProducts
